import {_keys, _setTimeout, currentTimeMillies, filterInstances, Hour, Minute, Module, Second} from "@intuitionrobotics/ts-common";
import {FirebaseModule, FirebaseSession} from "@intuitionrobotics/firebase/frontend";
import {ThunderDispatcher, ToastModule, XhrHttpModule} from "@intuitionrobotics/thunderstorm/frontend";
import {HttpMethod} from "@intuitionrobotics/thunderstorm/shared/types";
import {collection, CollectionReference, DocumentData, getDocs, onSnapshot, query, Query, QuerySnapshot, where} from "firebase/firestore";
import {GetFirebaseToken} from "@app-sp/app-shared/api";
import {UnitsModule, UnitView} from "@modules/UnitsModule";
import {FirebaseConfig} from "@intuitionrobotics/firebase";
import {DB_PairedUnit, DeviceIdentity, FullUnitConfig} from "@app/ir-q-app-common/types/units";
import {BaseRuntimeStatus} from "@app/ir-q-app-common/types/runtime-status";
import {DatabaseWrapper} from "@intuitionrobotics/firebase/app-frontend/database/DatabaseWrapper";
import {UnitsManagerModule} from "@modules/UnitsManagerModule";
import {PushMessagesModule} from "@modules/PushMessagesModule";
import {Elliq_ProductKey} from "@app/ir-q-app-common/shared/consts";
import {DB_PushMessage, Response_ListPushAndroid} from "@app/ir-q-app-common/types/fb-push-messages";
import {config} from "../config";
import {getUnitsPermissions} from "@consts/utils";
import {UiEnvItem} from "@modules/DataEnvsModule";
import {EnvironmentModule} from "@modules/EnvironmentModule";

export interface OnVisibilityChange {
    __onVisibilityChange: (visibilityState: DocumentVisibilityState) => void
}

const visibilityDispatcher = new ThunderDispatcher<OnVisibilityChange, "__onVisibilityChange">("__onVisibilityChange");

export interface OnPairsRetrieved {
    __onPairsRetrieved: (unitConfigs: FullUnitConfig[]) => void
}

const onPairsRetrievedDispatcher = new ThunderDispatcher<OnPairsRetrieved, "__onPairsRetrieved">("__onPairsRetrieved");

type Subscriptions = "ks" | "ksView" | "pushResult";

class FirebaseListenerModule_Class
    extends Module {
    private callbacks: { [projectId: string]: ((app: FirebaseSession) => void)[] | undefined } = {};
    private gettingTokenFor: { [projectId: string]: boolean | undefined } = {};

    // private isRuntimeStatusLoaded: boolean = false;

    constructor() {
        super("FirebaseListenerModule");
        document.onvisibilitychange = () => {
            visibilityDispatcher.dispatchUI(document.visibilityState);
        }
    }


    private subscriptions: { [k in Subscriptions]?: () => void } = {};

    private sessions: { [project: string]: { app: FirebaseSession, exp: number } } = {}
    private sessionExpiration = Hour;
    private grace = 5 * Minute;

    public stopListening(id?: Subscriptions) {
        const keys = _keys(this.subscriptions);
        if (id) {
            this.subscriptions[id]?.()
            delete this.subscriptions[id];
            this.logInfo(`stop listening for ${id}`)
            return;
        }

        keys.forEach(sub => {
            this.subscriptions[sub]?.()
            delete this.subscriptions[sub];
            this.logInfo(`stop listening for ${sub}`)
        })
        this.callbacks = {};
    }

    public listenImplForRS = (_onCallStatusesUpdated: (rs: any) => void, unitId: string) => {
        this.logInfo('starting listening for rs ' + unitId)
        // Offline: runtime status is seeded into UnitsModule (seedOffline) rather than
        // streamed from Firebase — deliver the seeded value instead of opening a
        // (token-requiring) realtime listener.
        if (EnvironmentModule.isOffline())
            return _onCallStatusesUpdated(UnitsModule.getRuntimeStatus(unitId));
        // @ts-ignore
        const options = FirebaseModule.config.ks;

        const rsCallback = (val: any) => {
            UnitsModule.setRSForUnit(unitId, val);
            _onCallStatusesUpdated(val)
        };
        this.onAppRetrieved(options.projectId, async (app) => {
            const db = await app.getDatabase();
            this.subscriptions["ks"]?.();
            const handleError = (e1: Error) => {
                this.logError(e1)
                ToastModule.toastInfo("Something went wrong, real time updates are disabled, Refresh to get them")
                db.listenWithError(
                    "__shallow-status" + (unitId ? "/" + unitId : ""),
                    rsCallback,
                    (e2: Error) => {
                        this.logError(e2)
                        ToastModule.toastError("Something went wrong, Please refresh")
                    }, true)
            };
            this.subscriptions["ks"] = db.listenWithError(
                "__shallow-status" + (unitId ? "/" + unitId : ""),
                rsCallback,
                handleError
            );
        })
        this.getApp(options)
    };

    public listenImplForPushResult = (updateCall: (response: Response_ListPushAndroid) => void, unitId: string) => {
        this.logInfo('starting listening for pushResult ' + unitId)

        // Offline: no realtime push stream (needs a Firebase client session). Build the
        // committed push history from fixtures and deliver it. The fixture is
        // dynamically imported so it stays code-split out of the online bundle, and
        // this runs at page mount (after Thunder.build), so upsertPushListResponse's
        // UI dispatch is safe (the dispatcher resolver is wired by build()).
        if (EnvironmentModule.isOffline()) {
            const unitBasic = UnitsManagerModule.getUnitBasic(unitId);
            if (!unitBasic)
                return;
            void import("../app/pages/units/offline/fixtures").then(({offlinePushMessages}) => {
                const response = offlinePushMessages(unitBasic.somSerial, unitBasic.tabletSerial);
                PushMessagesModule.upsertPushListResponse(response, {unitId, product: Elliq_ProductKey});
                updateCall(response);
            });
            return;
        }

        const options = config.FirebaseModule.pushResult;
        this.onAppRetrieved(options.projectId, async (app) => {

            const firestore = await app.getFirestore();

            const collectionName = "android-processed-push-messages";
            const col = collection(firestore, collectionName) as CollectionReference<DB_PushMessage>;

            const unitBasic = UnitsManagerModule.getUnitBasic(unitId);
            if (!unitBasic) {
                setTimeout(() => {
                    this.listenImplForPushResult(updateCall, unitId)
                }, 1000);
                return;
            }

            const q = query(col, where("deviceId", "in", [unitBasic.somSerial, unitBasic.tabletSerial]));
            const onNext = (snapshot: QuerySnapshot<DB_PushMessage>) => {
                const response = snapshot.docs.reduce((acc: Response_ListPushAndroid, doc) => {
                    const data = doc.data();
                    acc[data.deviceId].push({...data, env: config.EnvironmentModule.envName});
                    return acc;
                }, {[unitBasic.somSerial]: [], [unitBasic.tabletSerial]: []});
                PushMessagesModule.upsertPushListResponse(response, {unitId: unitId, product: Elliq_ProductKey});
                updateCall(response)
            };
            this.subscriptions["pushResult"]?.();
            this.subscriptions["pushResult"] = onSnapshot(q, onNext, async (e1) => {
                this.logError(e1)
                ToastModule.toastInfo("Something went wrong, real time updates are disabled, Refresh to get them")
                try {
                    const documentDataQuerySnapshot = await getDocs(q);
                    onNext(documentDataQuerySnapshot);
                } catch (e2) {
                    this.logError(e2)
                    ToastModule.toastError("Something went wrong, Please refresh")
                }
            });
        })
        this.getApp(options)
    };

    public listenImplForRSView = (_onCallStatusesUpdated: () => void) => {
        this.logInfo('starting listening for ksView')
        // @ts-ignore
        const options = FirebaseModule.config.ks;

        this.onAppRetrieved(options.projectId, async (app) => {
            const firestore = await app.getFirestore();
            this.subscriptions["ksView"]?.();

            const collectionName = 'rs-view';
            const col = collection(firestore, collectionName);

            // Scope to this deployment's env. The rs-view collection lives in the shared
            // elliq-env-map project and holds EVERY environment's units (~3.6k, ~88% prod);
            // the support tool only ever shows its own env, so listening to the whole
            // collection wasted ~10x bandwidth/memory and turned other-env churn into
            // re-render storms. UnitsModule.getEnv() is "elliq-env-<env>" (matches the doc
            // field). Single-field equality → no composite index needed.
            const q: Query<UnitView> = query(col, where("env", "==", UnitsModule.getEnv()));
            const onNext = (snapshot: QuerySnapshot<UnitView>) => {
                const unitViews = snapshot.docs.reduce((acc: Map<string, UnitView | undefined>, doc) => acc.set(doc.id, doc.data()), new Map());
                UnitsModule.setUnitViews(unitViews);
                _onCallStatusesUpdated()
                // if (!this.isRuntimeStatusLoaded)
                //     this.getRsOnce(app.getDatabase(), _onCallStatusesUpdated);
            };
            this.subscriptions["ksView"] = onSnapshot(q, onNext, async (e1) => {
                this.logError(e1)
                ToastModule.toastInfo("Something went wrong, real time updates are disabled, Refresh to get them")
                try {
                    const documentDataQuerySnapshot = await getDocs(q);
                    onNext(documentDataQuerySnapshot);
                } catch (e2) {
                    this.logError(e2)
                    ToastModule.toastError("Something went wrong, Please refresh")
                }
            });
        })
        this.getApp(options)
    };


    private getRsOnce = (db: DatabaseWrapper, callback: () => void) => {
        // this.isRuntimeStatusLoaded = true;
        db.get<{ [unitId: string]: BaseRuntimeStatus }>("__shallow-status")
            .then((d: { [unitId: string]: BaseRuntimeStatus } | null) => {
                d && UnitsModule.setRS(d);
                callback();
            })
            .catch(e => {
                // this.isRuntimeStatusLoaded = false;
                _setTimeout(() => {
                    this.getRsOnce(db, callback)
                }, 5 * Second)
            });
    };

    public getRsSync = async () => {
        return new Promise<void>((resolve, reject) => {
            // @ts-ignore
            const options = FirebaseModule.config.ks;
            this.onAppRetrieved(options.projectId, async (app) => {
                const db = await app.getDatabase();
                this.getRsOnce(db, resolve);
            })
            this.getApp(options)
        })
    }

    public getFromDBPairsAndDevices = (userGroupTypesDocs: UiEnvItem[], _onCallStatusesUpdated: () => void) => {
        // @ts-ignore
        const options = FirebaseModule.config.ks;
        this.onAppRetrieved(options.projectId, async (app) => {
            const firestore = await app.getFirestore();
            const colPairs = collection(firestore, "paired-units");
            const colDevices = collection(firestore, "device-identity");
            // const pair = unitId && product ? UnitsModule.getIdentity(unitId, product) : undefined;
            const qPair: Query<DocumentData> = query(colPairs);
            const qDevices: Query<DocumentData> = query(colDevices);

            let docSnapPair, docSnapDevices;
            try {
                [docSnapPair, docSnapDevices] = await Promise.all([
                    getDocs(qPair),
                    getDocs(qDevices)
                ])
            } catch (e) {
                this.logError("Failed to get pair list, trying again", e)
                ToastModule.toastError("Failed to get pair list, trying again")
                return _setTimeout(() => {
                    this.getFromDBPairsAndDevices(userGroupTypesDocs, _onCallStatusesUpdated)
                }, 5 * Second)
            }

            const deviceMap: { [sha256: string]: DeviceIdentity } = {}
            docSnapDevices.forEach(device => {
                const documentData = device.data() as DeviceIdentity;
                if (!documentData)
                    return;

                deviceMap[documentData.sha256] = documentData;
            })

            const relevantPairs: DB_PairedUnit[] = docSnapPair.docs.reduce((acc: DB_PairedUnit[], pair) => {
                const documentData = pair.data() as DB_PairedUnit;
                if (!documentData)
                    return acc;

                const unitsPermissions = getUnitsPermissions();
                if (!unitsPermissions)
                    return acc;

                const {unitIds, userGroups, userGroupTypes} = unitsPermissions.read;
                if (unitIds?.includes(".*") || unitIds?.includes(documentData.unitId)) {
                    acc.push(documentData)
                    return acc;
                }

                const unitEnv = documentData.comment;
                if (!unitEnv)
                    return acc;

                if (userGroups?.includes(unitEnv)) {
                    acc.push(documentData);
                    return acc;
                }

                const unitUserGroupType = userGroupTypesDocs.find(e => e.label === unitEnv)?.type;
                if (unitUserGroupType && userGroupTypes?.includes(unitUserGroupType)) {
                    acc.push(documentData);
                }


                return acc;
            }, []);

            const fullUnitConfigs = relevantPairs.reduce((acc: FullUnitConfig[], pair) => {
                const identities = filterInstances(pair.sha256.map(s => deviceMap[s]));
                if (identities.length !== pair.sha256.length) {
                    this.logWarning("Ignoring pair " + pair.unitId + ' since it\'s missing a device identity')
                    return acc;
                }
                const fullUnitConfig: FullUnitConfig = {
                    ...pair,
                    identities
                }

                acc.push(fullUnitConfig);
                return acc;
            }, []);
            onPairsRetrievedDispatcher.dispatchModule(fullUnitConfigs);
            onPairsRetrievedDispatcher.dispatchUI(fullUnitConfigs);
            _onCallStatusesUpdated()
        })
        this.getApp(options)
    };

    private onAppRetrieved(projectId: string, callBack: (app: FirebaseSession) => void): void {
        const session = this.sessions[projectId];
        if (session && session.exp > currentTimeMillies() + this.grace)
            return callBack(session.app);

        let callBacks = this.callbacks[projectId];
        if (!callBacks)
            callBacks = this.callbacks[projectId] = [];

        callBacks.push(callBack)
    }

    executeCallbacks(projectId: string, app: FirebaseSession) {
        const callbacks = this.callbacks[projectId];
        if (!callbacks)
            return;

        callbacks.forEach(c => c(app));
        this.callbacks[projectId] = [];
    }

    private getApp(_options: FirebaseConfig): void {
        if (this.gettingTokenFor[_options.projectId])
            return;

        const options = {..._options};
        const session = this.sessions[options.projectId];
        if (session && session.exp > currentTimeMillies() + this.grace)
            return this.executeCallbacks(options.projectId, session.app)

        new Promise<void>(async resolve => {
            this.gettingTokenFor[options.projectId] = true;
            const r = await XhrHttpModule
                .createRequest<GetFirebaseToken>(HttpMethod.GET, "get-firebase-token")
                .setUrlParams({projectId: options.projectId})
                .setRelativeUrl(`/v1/token/get`)
                .setTimeout(60 * Second)
                .setLabel(`Listening to collection`)
                .setOnError(`Failed to listen to collection`)
                .executeSync();

            const app = await FirebaseModule.createSession(options, r.token)
            this.sessions[options.projectId] = {app, exp: currentTimeMillies() + this.sessionExpiration};
            this.gettingTokenFor[options.projectId] = false;
            this.executeCallbacks(options.projectId, app)
            resolve();
        }).catch(e => {
            this.logError(e);
            _setTimeout(() => {
                this.getApp(_options);
            }, 5 * Second)
        })

    }
}

export const FirebaseListenerModule = new FirebaseListenerModule_Class();
